/**
 * Mermaid flowchart parser using mermaid.js internal API.
 *
 * Falls back to regex parsing if mermaid.js API is unavailable
 * (e.g. in Node test environment without DOM).
 */

import type { IRGraph, IRNode, IREdge, IRLane, Direction } from '../types';
import { NodeShape, createEmptyGraph } from '../types';
import { SHAPE_REGISTRY } from '../registry';

// Lazy-loaded mermaid instance
let mermaidInstance: typeof import('mermaid')['default'] | null = null;

async function getMermaid() {
  if (!mermaidInstance) {
    const mod = await import('mermaid');
    mermaidInstance = mod.default;
    mermaidInstance.initialize({
      startOnLoad: false,
      logLevel: 5 as any, // silent
    });
  }
  return mermaidInstance;
}

/** Shape classification for mermaid node types */
function classifyShape(type?: string): NodeShape {
  if (!type) return NodeShape.Process;
  switch (type) {
    case 'stadium':
      return NodeShape.Stadium;
    case 'round':
      return NodeShape.Process;
    case 'diamond':
    case 'question':
      return NodeShape.Decision;
    case 'cylinder':
      return NodeShape.Datastore;
    case 'circle':
    case 'doublecircle':
      return NodeShape.StartCircle;
    case 'subroutine':
      return NodeShape.Subroutine;
    default:
      return NodeShape.Process;
  }
}

/** Try mermaid.js API first, fall back to regex */
export async function parseMermaidFlowchart(text: string): Promise<IRGraph> {
  try {
    return await parseMermaidApi(text);
  } catch {
    return parseRegex(text);
  }
}

/** Parse using mermaid.js internal API */
async function parseMermaidApi(text: string): Promise<IRGraph> {
  const mermaid = await getMermaid();

  // Prepend %%{init:...}%% for security mode
  const diagram = await mermaid.mermaidAPI.getDiagramFromText(text);
  const db = (diagram as any).db;

  // Extract direction
  const dir = (db.getDirection?.() ?? 'TD') as Direction;
  const graph = createEmptyGraph(dir, 'flowchart');

  // Extract vertices
  const vertices: Map<string, any> =
    db.getVertices?.() ?? new Map();

  for (const [id, v] of vertices) {
    const shape = classifyShape(v.type);
    const entry = SHAPE_REGISTRY[shape];
    const label = v.text ?? v.labelText ?? id;
    const node: IRNode = {
      id,
      label: stripHtml(label),
      shape,
      width: entry.defaultWidth,
      height: entry.defaultHeight,
      meta: { mermaidType: v.type },
    };
    graph.nodes.set(id, node);
  }

  // Extract edges
  const edges: any[] = db.getEdges?.() ?? [];
  let edgeCounter = 0;
  for (const e of edges) {
    edgeCounter++;
    const edge: IREdge = {
      id: `e${edgeCounter}`,
      source: e.start,
      target: e.end,
      label: e.text || undefined,
      dashed: e.stroke === 'dotted',
    };
    graph.edges.push(edge);
  }

  // Extract subgraphs as lanes
  const subGraphs: any[] = db.getSubGraphs?.() ?? [];
  const subgraphIds = new Set(subGraphs.map((sg) => sg.id));

  for (const sg of subGraphs) {
    // Filter out nested subgraph IDs from node lists
    const nodeIds = (sg.nodes ?? []).filter((nid: string) => !subgraphIds.has(nid));
    const lane: IRLane = {
      id: sg.id,
      title: sg.title ?? sg.id,
      nodeIds,
      x: 0,
      y: 0,
      width: 0,
      height: 0,
    };
    graph.lanes.push(lane);
    // Assign lane to nodes
    for (const nid of lane.nodeIds) {
      const node = graph.nodes.get(nid);
      if (node) node.laneId = sg.id;
    }
  }

  // Remove subgraph IDs from the node map — they are containers, not action nodes
  for (const sgId of subgraphIds) {
    graph.nodes.delete(sgId);
  }

  // Handle orphan nodes
  addOrphanLane(graph);

  return graph;
}

// ─── Regex fallback ──────────────────────────────────────────────

const NODE_PATTERNS: Array<{ re: RegExp; shape: NodeShape; isTerminal?: 'start' | 'end' }> = [
  // (((text))) - end node / double circle
  { re: /([A-Za-z_]\w*)\(\(\((.+?)\)\)\)/g, shape: NodeShape.EndCircle, isTerminal: 'end' },
  // ((text)) - start node / circle
  { re: /([A-Za-z_]\w*)\(\((.+?)\)\)/g, shape: NodeShape.StartCircle, isTerminal: 'start' },
  // [(text)] - datastore / cylinder
  { re: /([A-Za-z_]\w*)\[\((.+?)\)\]/g, shape: NodeShape.Datastore },
  // ([text]) - action alt / stadium → pill shape (activity rules convert to circles)
  { re: /([A-Za-z_]\w*)\(\[(.+?)\]\)/g, shape: NodeShape.Stadium },
  // {text} - decision
  { re: /([A-Za-z_]\w*)\{(.+?)\}/g, shape: NodeShape.Decision },
  // [text] - action / rectangle
  { re: /([A-Za-z_]\w*)\[(.+?)\]/g, shape: NodeShape.Process },
];

const EDGE_PIPE = /([A-Za-z_]\w*)(?:\([^)]*\)|\[[^\]]*\]|\{[^}]*\})?\s+(--+>|-\.+->)\s*(?:\|([^|]*)\|\s*)?([A-Za-z_]\w*)/g;
const EDGE_DASH = /([A-Za-z_]\w*)(?:\([^)]*\)|\[[^\]]*\]|\{[^}]*\})?\s+(-\.|--)\s+([^-].+?)\s+(-->|\.->)\s*([A-Za-z_]\w*)/g;
const SUBGRAPH_START = /subgraph\s+(\w+)(?:\s*\["?(.+?)"?\])?/;
const SUBGRAPH_END = /^\s*end\s*$/;
const DIRECTION_RE = /flowchart\s+(TD|TB|LR|RL|BT)/;

/**
 * Expand chained edges: "A[X] --> B[Y] --> C[Z]" becomes two lines:
 * "A[X] --> B[Y]" and "B[Y] --> C[Z]"
 */
function expandChains(text: string): string {
  // Regex to find chains: node+shape --> node+shape --> node+shape
  const NODE_TOKEN = /[A-Za-z_]\w*(?:\(\(\(.*?\)\)\)|\(\(.*?\)\)|\[\(.*?\)\]|\(\[.*?\]\)|\{.*?\}|\[.*?\])?/;
  const ARROW = /\s+(?:-->|-\.->)\s*/;
  // Build a full chain pattern
  const chainRe = new RegExp(
    `(${NODE_TOKEN.source})(${ARROW.source}${NODE_TOKEN.source}){2,}`,
    'g',
  );

  return text.replace(chainRe, (match) => {
    // Split the chain into individual node tokens
    const parts = match.split(/(-->|-\.->)/);
    const tokens: string[] = [];
    for (const p of parts) {
      const trimmed = p.trim();
      if (trimmed && trimmed !== '-->' && trimmed !== '-.->') {
        tokens.push(trimmed);
      }
    }
    // Detect arrow types
    const arrows: string[] = [];
    for (const p of parts) {
      const trimmed = p.trim();
      if (trimmed === '-->' || trimmed === '-.->') {
        arrows.push(trimmed);
      }
    }

    const lines: string[] = [];
    for (let i = 0; i < tokens.length - 1; i++) {
      const arrow = arrows[i] ?? '-->';
      lines.push(`${tokens[i]} ${arrow} ${tokens[i + 1]}`);
    }
    return lines.join('\n    ');
  });
}

function parseRegex(text: string): IRGraph {
  const preprocessed = expandChains(text);
  const lines = preprocessed.trim().split('\n');
  const graph = createEmptyGraph('TD', 'flowchart');
  const nodeMap = new Map<string, { text: string; shape: NodeShape; isTerminal?: 'start' | 'end' }>();
  let currentLane: IRLane | null = null;

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line || line.startsWith('%%')) continue;

    // Direction
    const dirMatch = DIRECTION_RE.exec(line);
    if (dirMatch) {
      graph.direction = dirMatch[1] as Direction;
      continue;
    }

    // Subgraph start
    const sgMatch = SUBGRAPH_START.exec(line);
    if (sgMatch) {
      currentLane = {
        id: sgMatch[1],
        title: sgMatch[2] ?? sgMatch[1],
        nodeIds: [],
        x: 0, y: 0, width: 0, height: 0,
      };
      graph.lanes.push(currentLane);
      continue;
    }

    // Subgraph end
    if (SUBGRAPH_END.test(line)) {
      currentLane = null;
      continue;
    }

    if (line.startsWith('flowchart') || line.startsWith('graph') || line.startsWith('style ')) continue;

    // Extract nodes from line
    for (const { re, shape, isTerminal } of NODE_PATTERNS) {
      re.lastIndex = 0;
      let m: RegExpExecArray | null;
      while ((m = re.exec(line)) !== null) {
        const id = m[1];
        const nodeText = m[2].trim();
        if (!nodeMap.has(id)) {
          nodeMap.set(id, { text: nodeText, shape, isTerminal });
          if (currentLane) currentLane.nodeIds.push(id);
        }
      }
    }

    // Extract edges — try dash-label style first (more specific: A -- label --> B)
    let foundDash = false;
    EDGE_DASH.lastIndex = 0;
    let em: RegExpExecArray | null;
    while ((em = EDGE_DASH.exec(line)) !== null) {
      graph.edges.push({
        id: `e${graph.edges.length + 1}`,
        source: em[1],
        target: em[5],
        label: em[3]?.trim() || undefined,
        dashed: em[2] === '-.',
      });
      foundDash = true;
    }

    // Only try pipe style if no dash-label edges found on this line
    if (!foundDash) {
      EDGE_PIPE.lastIndex = 0;
      while ((em = EDGE_PIPE.exec(line)) !== null) {
        graph.edges.push({
          id: `e${graph.edges.length + 1}`,
          source: em[1],
          target: em[4],
          label: em[3]?.trim() || undefined,
          dashed: em[2].includes('.'),
        });
      }
    }
  }

  // Build IR nodes — skip subgraph IDs (they are containers, not action nodes)
  const laneIds = new Set(graph.lanes.map((l) => l.id));
  for (const [id, info] of nodeMap) {
    if (laneIds.has(id)) continue;
    const entry = SHAPE_REGISTRY[info.shape];
    const node: IRNode = {
      id,
      label: info.text,
      shape: info.shape,
      width: entry.defaultWidth,
      height: entry.defaultHeight,
      meta: { isTerminal: info.isTerminal },
    };
    // Assign lane
    for (const lane of graph.lanes) {
      if (lane.nodeIds.includes(id)) {
        node.laneId = lane.id;
        break;
      }
    }
    graph.nodes.set(id, node);
  }

  addOrphanLane(graph);
  return graph;
}

/** Create a "General" lane for nodes not in any subgraph */
function addOrphanLane(graph: IRGraph): void {
  const laneNodeIds = new Set(graph.lanes.flatMap((l) => l.nodeIds));
  const orphans = [...graph.nodes.keys()].filter((id) => !laneNodeIds.has(id));
  if (orphans.length > 0) {
    const lane: IRLane = {
      id: '_general',
      title: 'General',
      nodeIds: orphans,
      x: 0, y: 0, width: 0, height: 0,
    };
    graph.lanes.push(lane);
    for (const id of orphans) {
      const node = graph.nodes.get(id);
      if (node) node.laneId = '_general';
    }
  }
}


function stripHtml(s: string): string {
  return s.replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]+>/g, '');
}
